/**
 * Find sibling elements that can receive the same architect-supplied facts.
 * Conservative: same rule scope, same similarity group, missing the same keys,
 * no model or user value already present.
 */
import {
  type EvaluatedElement,
  evaluateRule,
  type RuleSpec,
} from "@vera/rules";
import type { UserFactValue } from "../stores/userFactStore";
import { mergeElementWithUserFacts } from "../stores/userFactStore";
import { factPresent, missingFactsForEvaluation } from "./missingFacts";

export interface BulkApplyCandidate {
  dbId: number;
  displayName: string;
  /** Subset of applied fact keys that would be copied to this element. */
  factKeys: string[];
}

export interface AppliedFactSlice {
  factKey: string;
  label?: string;
  value: UserFactValue;
}

/** Revit-style family label from displayName, e.g. "Single-Flush [422466]" → "Single-Flush". */
export function elementFamilyLabel(el: EvaluatedElement): string {
  const name = el.displayName ?? "";
  const m = name.match(/^(.+?)\s*\[\d+]/);
  if (m?.[1]) return m[1].trim();
  return name.trim() || `Element ${el.dbId}`;
}

function inSameSimilarityGroup(
  source: EvaluatedElement,
  target: EvaluatedElement
): boolean {
  const bracket = /\[\d+]/;
  const sourceBracket = bracket.test(source.displayName ?? "");
  const targetBracket = bracket.test(target.displayName ?? "");
  if (sourceBracket && targetBracket) {
    return elementFamilyLabel(source) === elementFamilyLabel(target);
  }
  return source.ontologyType === target.ontologyType;
}

export function findBulkApplyCandidates(input: {
  rule: RuleSpec;
  elements: EvaluatedElement[];
  sourceDbId: number;
  appliedFacts: AppliedFactSlice[];
  allOverrides: Record<number, Record<string, UserFactValue>>;
}): BulkApplyCandidate[] {
  const { rule, elements, sourceDbId, appliedFacts, allOverrides } = input;
  if (appliedFacts.length === 0) return [];

  const source = elements.find(e => e.dbId === sourceDbId);
  if (!source) return [];

  const appliedKeys = appliedFacts.map(f => f.factKey);
  const out: BulkApplyCandidate[] = [];

  for (const el of elements) {
    if (el.dbId === sourceDbId) continue;
    if (el.ontologyType !== rule.appliesTo.elementClass) continue;
    if (!inSameSimilarityGroup(source, el)) continue;

    const targetOverrides = allOverrides[el.dbId] ?? {};
    const mergedFacts = { ...el.facts, ...targetOverrides };
    const missingKeys = new Set(
      missingFactsForEvaluation(rule, mergedFacts).map(m => m.factKey)
    );

    const factKeys: string[] = [];
    for (const key of appliedKeys) {
      if (factPresent(el.facts, key)) continue;
      if (targetOverrides[key] !== undefined) continue;
      if (!missingKeys.has(key)) continue;
      factKeys.push(key);
    }
    if (factKeys.length === 0) continue;

    const evalResult = evaluateRule(
      rule,
      mergeElementWithUserFacts(el, allOverrides)
    );
    if (evalResult === null) continue;

    out.push({
      dbId: el.dbId,
      displayName: el.displayName ?? `dbId ${el.dbId}`,
      factKeys,
    });
  }

  return out.sort((a, b) => a.displayName.localeCompare(b.displayName));
}
